import { DateTime } from 'luxon';
import { call, put, select, takeEvery } from 'redux-saga/effects';

import { PLAYOFFS_ID, REGULAR_SEASON_ID } from '../../../../../utility/constants';
import { statsConfigSelectors } from '../../../config';

import actionCreators from './actionCreators';
import actionTypes from './actionTypes';
import selectors from './selectors';

// INDIVIDUAL SAGAS

/* validateDateTo - set "dateTo" to today if current val is in future
 * Ex. In middle of the season will query from start until today
 * @modifies {state|playersAndTeams|report|current|dateFrom}
 */
export function* validateDateTo() {
    const { dateTo } = yield select(selectors.getCurrentReport);
    const today = DateTime.local();

    if (DateTime.fromISO(dateTo) > today) {
        yield put(actionCreators.resetReportSection('dateTo', today.toISODate()));
    }
}

/* setDateFromFromSeason - set "dateFrom" using
 * If reg season - use startDate, else regularSeasonEndDate
 * @modifies {state|playersAndTeams|report|current|dateFrom}
 */
export function* setDateFromUsingSeasonFrom() {
    const { gameTypeId, seasonFrom } = yield select(selectors.getCurrentReport);

    const config = yield select(statsConfigSelectors.getConfig);
    const seasonData = statsConfigSelectors.getDataForSeason(config, seasonFrom);

    const dateFrom = Number(gameTypeId) === REGULAR_SEASON_ID ? seasonData.startDate : seasonData.regularSeasonEndDate;

    yield put(actionCreators.resetReportSection('dateFrom', dateFrom));
}

/* setDateToUsingSeasonTo - set "dateTo" using
 * If reg season - use regularSeasonEndDate, else endDate
 * If either of the above is in the future, use today
 * @modifies {state|playersAndTeams|report|current|dateTo}
 */
export function* setDateToUsingSeasonTo() {
    const { gameTypeId, seasonTo } = yield select(selectors.getCurrentReport);

    const config = yield select(statsConfigSelectors.getConfig);
    const seasonData = statsConfigSelectors.getDataForSeason(config, seasonTo);

    const dateTo = Number(gameTypeId) === REGULAR_SEASON_ID ? seasonData.regularSeasonEndDate : seasonData.endDate;

    yield put(actionCreators.resetReportSection('dateTo', dateTo));
}

/* validateSeasonToWithSeasonFrom - correct "seasonTo" if it is less than "seasonFrom"
 * @modifies {state|playersAndTeams|report|current|seasonTo}
 */
export function* validateSeasonToWithSeasonFrom() {
    const { seasonFrom, seasonTo } = yield select(selectors.getCurrentReport);

    if (seasonFrom > seasonTo) {
        yield put(actionCreators.resetReportSection('seasonTo', seasonFrom));
    }
}

/* validateDateToUsingDateFrom - set dateFrom to dateTo if From > To
 * @modifies {state|playersAndTeams|report|current|dateTo}
 */
export function* validateDateToUsingDateFrom() {
    const { dateFrom, dateTo } = yield select(selectors.getCurrentReport);

    if (DateTime.fromISO(dateFrom) > DateTime.fromISO(dateTo)) {
        yield put(actionCreators.resetReportSection('dateFrom', dateTo));
    }
}

/* setDatesUsingGameTypeId - set dateFrom/To based on gameTypeId
 * @modifies {state|playersAndTeams|report|current|dateFrom}
 * @modifies {state|playersAndTeams|report|current|dateTo}
 */
export function* setDatesUsingGameTypeId() {
    const { gameTypeId, seasonFrom, seasonTo } = yield select(selectors.getCurrentReport);
    const config = yield select(statsConfigSelectors.getConfig);

    const seasonFromData = statsConfigSelectors.getDataForSeason(config, seasonFrom);
    const seasonToData = statsConfigSelectors.getDataForSeason(config, seasonTo);

    const dateFrom =
        Number(gameTypeId) === REGULAR_SEASON_ID ? seasonFromData.startDate : seasonFromData.regularSeasonEndDate;

    const dateTo = Number(gameTypeId) === REGULAR_SEASON_ID ? seasonToData.regularSeasonEndDate : seasonToData.endDate;

    yield put(actionCreators.resetReportSection('dateFrom', dateFrom));
    yield put(actionCreators.resetReportSection('dateTo', dateTo));
}

export function* validateReportIsForPlayoffs() {
    const { gameTypeId, reportName } = yield select(selectors.getCurrentReport);

    // If report is shootout, then gameType is changed to Playoffs, we will change reports to the default
    if (gameTypeId === PLAYOFFS_ID && reportName.includes('shootout')) {
        const defaultReportName = statsConfigSelectors.getDefaultReportName;
        yield put(actionCreators.resetReportSection('reportName', defaultReportName));
    }
}

// GROUPING INDIVIDUAL SAGAS TOGETHER
function* updateReportUsingSeasonFrom() {
    yield call(setDateFromUsingSeasonFrom);
    yield call(validateSeasonToWithSeasonFrom);
}

function* updateReportUsingSeasonTo() {
    yield call(setDateToUsingSeasonTo);
}

function* updateReportUsingDateFrom() {
    yield call(validateDateToUsingDateFrom);
}

function* updateReportUsingDateTo() {
    yield call(validateDateTo);
}

function* updateReportUsingGameTypeId() {
    yield call(setDatesUsingGameTypeId);
    yield call(validateReportIsForPlayoffs);
}

// WATCHERS / WATCHING SAGAS
function* watchSeasonFrom() {
    yield takeEvery(actionTypes.SET_SEASON_FROM, updateReportUsingSeasonFrom);
}

function* watchSeasonTo() {
    yield takeEvery(actionTypes.SET_SEASON_TO, updateReportUsingSeasonTo);
}

function* watchDateFrom() {
    yield takeEvery(actionTypes.SET_DATE_FROM, updateReportUsingDateFrom);
}

function* watchDateTo() {
    yield takeEvery(actionTypes.SET_DATE_TO, updateReportUsingDateTo);
}

function* watchGameTypeId() {
    yield takeEvery(actionTypes.SET_GAME_TYPE, updateReportUsingGameTypeId);
}

// EXPORT / OUTSIDE INTERFACE
export default {
    seasonFrom: watchSeasonFrom(),
    seasonTo: watchSeasonTo(),
    dateFrom: watchDateFrom(),
    dateTo: watchDateTo(),
    gameTypeId: watchGameTypeId(),
};
